import { useMemo, useState } from 'react';
import type { RevenueGroupSeries, RevenueSeriesGranularity, RevenueSeriesPoint } from '@nadoutong/contracts';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import {
  TREND_TONE_CYCLE,
  TrendBucketTable,
  TrendChart,
  TrendViewToggle,
  rollupDailyBuckets,
} from '../../components/charts/TrendChart.tsx';
import type {
  TrendChartSeries,
  TrendRollup,
  TrendTableColumn,
  TrendView,
} from '../../components/charts/TrendChart.tsx';
import {
  formatCnyCents,
  formatUsdMicrosAsCnyDisplay,
  usdMicrosFromCentsFallback,
  usdMicrosToCnyCents,
} from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, Chip, KpiCard, Panel, TBody, Table, Td, Th, THead, Tr } from '../../ui/index.ts';

/**
 * Shared internals for the 平台 SKU / 上游 SKU hub tabs (#1675): the
 * window-totals KPI strip, the sortable per-group aggregate table, and
 * ONE combined comparison trend chart (#1683 — every group is a line
 * series with a click-to-toggle legend; the per-group small-multiples
 * grid is gone). Both tabs still receive EVERY group — the operator
 * explicitly asked for no top-N truncation, so nothing here limits or
 * merges groups; with many groups only the DEFAULT visibility is
 * capped, never the data.
 *
 * Money display: charged revenue arrives as USD micros and is converted
 * at the DISPLAY FX rate (`usePricing`, same rule as the overview /
 * models tabs) — a missing rate withholds the charged bars/cells behind
 * an explicit note instead of rendering ¥0 lies. Upstream cost and L2
 * profit are ledger CNY cents and always render.
 */

const COUNT_FORMATTER = new Intl.NumberFormat('en-US');

/** One group (platform SKU / upstream) with its display name resolved. */
export interface MetricsGroup {
  /** Raw group identifier (`sku_id` / `upstream_id`) or `'(unknown)'`. */
  readonly key: string;
  /** Resolved display name; falls back to the raw identifier. */
  readonly name: string;
  readonly series: RevenueGroupSeries;
}

// ─── Window totals strip ─────────────────────────────────────────────

export function GroupTotalsStrip({
  groups,
  cnyPerUsd,
  testIdPrefix,
}: {
  readonly groups: readonly MetricsGroup[];
  readonly cnyPerUsd: number | null;
  readonly testIdPrefix: string;
}): JSX.Element {
  const t = useT();
  const totals = useMemo(() => {
    let calls = 0;
    let chargedMicros = 0;
    let costCents = 0;
    let profitCents = 0;
    let costMissing = 0;
    for (const group of groups) {
      calls += group.series.total_usage_count;
      chargedMicros += usdMicrosFromCentsFallback(
        group.series.total_usd_charged_micros,
        group.series.total_usd_charged_cents,
      );
      costCents += group.series.total_cny_upstream_cost_cents;
      profitCents += group.series.total_l2_profit_cny_cents;
      costMissing += group.series.total_cost_missing_count;
    }
    return { calls, chargedMicros, costCents, profitCents, costMissing };
  }, [groups]);

  return (
    <div className="flex flex-col gap-3" data-testid={`${testIdPrefix}-kpis`}>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          testId={`${testIdPrefix}-kpi-calls`}
          label={t('metrics.group.kpi.calls')}
          value={COUNT_FORMATTER.format(totals.calls)}
        />
        <KpiCard
          testId={`${testIdPrefix}-kpi-charged`}
          label={t('metrics.group.kpi.charged')}
          tone="ok"
          value={formatUsdMicrosAsCnyDisplay(totals.chargedMicros, cnyPerUsd, false)}
        />
        <KpiCard
          testId={`${testIdPrefix}-kpi-cost`}
          label={t('metrics.group.kpi.cost')}
          value={formatCnyCents(totals.costCents, false)}
        />
        <KpiCard
          testId={`${testIdPrefix}-kpi-profit`}
          label={t('metrics.group.kpi.profit')}
          tone={totals.profitCents < 0 ? 'bad' : 'default'}
          value={formatCnyCents(totals.profitCents, true)}
        />
      </div>
      {/* ADR-007: unknown cost ≠ 0 cost — rows the sums silently
          skipped are surfaced instead of understating margin quietly. */}
      {totals.costMissing > 0 ? (
        <div>
          <Chip tone="warn" testId={`${testIdPrefix}-cost-missing`}>
            {t('metrics.group.cost_missing', { count: totals.costMissing })}
          </Chip>
        </div>
      ) : null}
    </div>
  );
}

// ─── Aggregate table ─────────────────────────────────────────────────

/**
 * Normalised aggregate row. The platform-SKU tab derives these from the
 * series per-group totals (display-rate CNY conversion); the upstream
 * tab maps `/by-upstream` rows (per-ledger-row market-rate CNY). Fields
 * a source cannot provide are `null` and render `—`.
 */
export interface GroupAggregateRow {
  readonly key: string;
  readonly name: string;
  /** Raw identifier, shown as a sub-line when it differs from `name`. */
  readonly rawId: string | null;
  readonly usageCount: number;
  readonly chargedCnyCents: number | null;
  readonly costCnyCents: number;
  readonly profitCnyCents: number;
  readonly marginPct: number | null;
  readonly costMissingCount: number;
}

type GroupSortKey = 'charged' | 'profit' | 'calls';

interface GroupSort {
  readonly key: GroupSortKey;
  readonly dir: 'asc' | 'desc';
}

/** Matches the series ranking (charged DESC) so the initial render
 * agrees with the trend grid's ordering. */
const GROUP_SORT_DEFAULT: GroupSort = { key: 'charged', dir: 'desc' };

const GROUP_SORT_VALUE: Record<GroupSortKey, (row: GroupAggregateRow) => number> = {
  charged: (row) => row.chargedCnyCents ?? 0,
  profit: (row) => row.profitCnyCents,
  calls: (row) => row.usageCount,
};

function compareGroupRows(a: GroupAggregateRow, b: GroupAggregateRow, sort: GroupSort): number {
  let cmp = GROUP_SORT_VALUE[sort.key](a) - GROUP_SORT_VALUE[sort.key](b);
  if (cmp === 0) {
    // Deterministic tiebreak so equal values keep a stable order.
    cmp = a.name.localeCompare(b.name);
  }
  return sort.dir === 'asc' ? cmp : -cmp;
}

function SortableTh({
  label,
  sortKey,
  sort,
  onSort,
  testIdPrefix,
}: {
  readonly label: string;
  readonly sortKey: GroupSortKey;
  readonly sort: GroupSort;
  readonly onSort: (key: GroupSortKey) => void;
  readonly testIdPrefix: string;
}): JSX.Element {
  const active = sort.key === sortKey;
  const Indicator = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <Th num aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => {
          onSort(sortKey);
        }}
        className={[
          'inline-flex items-center gap-1 text-label font-medium transition-colors duration-120',
          active ? 'text-paper' : 'text-paper-low hover:text-paper',
        ].join(' ')}
        data-testid={`${testIdPrefix}-sort-${sortKey}`}
      >
        {label}
        <Indicator aria-hidden className={active ? 'h-3 w-3' : 'h-3 w-3 opacity-40'} />
      </button>
    </Th>
  );
}

export function GroupAggregateTable({
  caption,
  sub,
  note,
  rows,
  emptyLabel,
  testIdPrefix,
}: {
  readonly caption: string;
  readonly sub: string;
  readonly note: string;
  readonly rows: readonly GroupAggregateRow[];
  readonly emptyLabel: string;
  readonly testIdPrefix: string;
}): JSX.Element {
  const t = useT();
  const [sort, setSort] = useState<GroupSort>(GROUP_SORT_DEFAULT);
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => compareGroupRows(a, b, sort)),
    [rows, sort],
  );
  const toggleSort = (key: GroupSortKey): void => {
    setSort((previous) =>
      previous.key === key
        ? { key, dir: previous.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' },
    );
  };
  return (
    <Panel
      caption={caption}
      count={rows.length}
      actions={<span className="font-mono text-micro text-paper-low">{sub}</span>}
      testId={`${testIdPrefix}-table`}
    >
      {rows.length === 0 ? (
        <p className="m-0 p-4 text-body text-paper-low" data-testid={`${testIdPrefix}-table-empty`}>
          {emptyLabel}
        </p>
      ) : (
        <>
          <Table className="border-none">
            <THead>
              <Tr interactive={false}>
                <Th>{t('metrics.group.col.name')}</Th>
                <SortableTh
                  label={t('metrics.group.col.calls')}
                  sortKey="calls"
                  sort={sort}
                  onSort={toggleSort}
                  testIdPrefix={testIdPrefix}
                />
                <SortableTh
                  label={t('metrics.group.col.charged')}
                  sortKey="charged"
                  sort={sort}
                  onSort={toggleSort}
                  testIdPrefix={testIdPrefix}
                />
                <Th num>{t('metrics.group.col.cost')}</Th>
                <SortableTh
                  label={t('metrics.group.col.profit')}
                  sortKey="profit"
                  sort={sort}
                  onSort={toggleSort}
                  testIdPrefix={testIdPrefix}
                />
                <Th num>{t('metrics.group.col.margin')}</Th>
                <Th num>{t('metrics.group.col.cost_missing')}</Th>
              </Tr>
            </THead>
            <TBody>
              {sortedRows.map((row) => (
                <Tr key={row.key} data-testid={`${testIdPrefix}-row-${row.key}`}>
                  <Td>
                    <div className="font-mono font-medium text-paper">{row.name}</div>
                    {row.rawId !== null && row.rawId !== row.name ? (
                      <div className="font-mono text-micro text-paper-low">{row.rawId}</div>
                    ) : null}
                  </Td>
                  <Td num>{COUNT_FORMATTER.format(row.usageCount)}</Td>
                  <Td num className="text-ok">
                    {row.chargedCnyCents === null ? '—' : formatCnyCents(row.chargedCnyCents, false)}
                  </Td>
                  <Td num className="text-bad">
                    {formatCnyCents(row.costCnyCents, false)}
                  </Td>
                  <Td num className={row.profitCnyCents < 0 ? 'text-bad' : undefined}>
                    {formatCnyCents(row.profitCnyCents, true)}
                  </Td>
                  <Td num>
                    {row.marginPct === null ? '—' : `${(row.marginPct * 100).toFixed(1)}%`}
                  </Td>
                  <Td num className={row.costMissingCount > 0 ? 'text-warn' : 'text-paper-low'}>
                    {COUNT_FORMATTER.format(row.costMissingCount)}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
          <p
            className="m-0 border-t border-line-soft px-4 py-2.5 text-micro text-paper-low"
            data-testid={`${testIdPrefix}-table-note`}
          >
            {note}
          </p>
        </>
      )}
    </Panel>
  );
}

// ─── Combined comparison chart (#1683) ───────────────────────────────

/**
 * What every series of the combined chart plots. 收费 needs the display
 * FX rate (USD micros → display CNY); 调用数 and L2 利润 come straight
 * off the points (profit is ledger CNY cents, no rate involved).
 */
export type GroupTrendMetric = 'charged' | 'calls' | 'profit';

const GROUP_METRIC_OPTIONS: readonly GroupTrendMetric[] = ['charged', 'calls', 'profit'];
const GROUP_METRIC_KEYS: Record<GroupTrendMetric, I18nKey> = {
  charged: 'metrics.group.metric.charged',
  calls: 'metrics.group.metric.calls',
  profit: 'metrics.group.metric.profit',
};

/**
 * Pristine-default visibility cap: with more groups than this, only the
 * top N by charged start visible (the API already ranks series charged
 * DESC) and an explicit note says so — 30 always-on lines would be
 * unreadable, but every group stays one legend click away (the no-top-N
 * mandate is about DATA availability, which is untouched).
 */
const DEFAULT_VISIBLE_LIMIT = 10;

/**
 * Per-bucket charged CNY values for one group, or `null` when the
 * display rate is unavailable (the caller withholds the chart behind an
 * explicit note — a ¥0 series would be a lie, same rule as the overview
 * trend).
 */
function chargedCnySeries(
  points: readonly RevenueSeriesPoint[],
  cnyPerUsd: number | null,
): readonly number[] | null {
  const values: number[] = [];
  for (const point of points) {
    const cnyCents = usdMicrosToCnyCents(
      usdMicrosFromCentsFallback(point.usd_charged_micros, point.usd_charged_cents),
      cnyPerUsd,
    );
    if (cnyCents === null) {
      return null;
    }
    values.push(cnyCents);
  }
  return values;
}

/** Metric values per bucket, or `null` when 收费 lacks a display rate. */
function metricSeriesValues(
  group: MetricsGroup,
  metric: GroupTrendMetric,
  cnyPerUsd: number | null,
): readonly number[] | null {
  if (metric === 'calls') {
    return group.series.points.map((point) => point.usage_count);
  }
  if (metric === 'profit') {
    return group.series.points.map((point) => point.l2_profit_cny_cents);
  }
  return chargedCnySeries(group.series.points, cnyPerUsd);
}

/**
 * ONE full-width comparison chart for all groups (#1683, replacing the
 * small-multiples grid): every group is a line series colored from
 * `TREND_TONE_CYCLE` by its position in the charged-DESC ranking (the
 * cycle repeats past 8, but each series keeps ONE tone across legend
 * chip, line, and tooltip swatch), with an interactive legend (click to
 * toggle, 全选/清空), a metric switcher (收费 / 调用数 / L2 利润), and
 * the 表格 twin that follows the SAME visibility + metric selection.
 */
export function GroupTrendsPanel({
  title,
  groups,
  granularity,
  cnyPerUsd,
  emptyLabel,
  testIdPrefix,
}: {
  readonly title: string;
  readonly groups: readonly MetricsGroup[];
  readonly granularity: RevenueSeriesGranularity;
  /** Validated display rate, or null (the 收费 metric is withheld). */
  readonly cnyPerUsd: number | null;
  readonly emptyLabel: string;
  readonly testIdPrefix: string;
}): JSX.Element {
  const t = useT();
  const [view, setView] = useState<TrendView>('chart');
  const [rollup, setRollup] = useState<TrendRollup>('day');
  const [metric, setMetric] = useState<GroupTrendMetric>('charged');
  // `null` = pristine default (top-N by charged visible). The first
  // toggle materialises the effective set and edits a COPY of it.
  const [hiddenOverride, setHiddenOverride] = useState<ReadonlySet<string> | null>(null);

  const defaultHidden = useMemo(
    () => new Set(groups.slice(DEFAULT_VISIBLE_LIMIT).map((group) => group.key)),
    [groups],
  );
  const hiddenKeys = hiddenOverride ?? defaultHidden;
  const toggleSeries = (key: string): void => {
    setHiddenOverride((previous) => {
      const next = new Set(previous ?? defaultHidden);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const bucketLabels = groups[0]?.series.points.map((point) => point.bucket) ?? [];
  // Hourly buckets are labelled `YYYY-MM-DD HH:00`; when the whole
  // window sits inside one UTC+8 day the date part is pure noise, so
  // the axis shows bare `HH:00` (the note strip above names the day).
  const singleDay =
    granularity === 'hour' &&
    bucketLabels.length > 0 &&
    bucketLabels.every((label) => label.slice(0, 10) === bucketLabels[0]?.slice(0, 10));
  const displayLabels = singleDay ? bucketLabels.map((label) => label.slice(11)) : bucketLabels;

  const valuesByGroup = groups.map((group) => metricSeriesValues(group, metric, cnyPerUsd));
  // Only 收费 can be unavailable (missing display rate); the whole
  // chart/table is then withheld behind an explicit note — the other
  // two metrics stay one pill-click away.
  const metricAvailable = valuesByGroup.every((values) => values !== null);
  const chartSeries: readonly TrendChartSeries[] = groups.map((group, index) => ({
    key: group.key,
    label: group.name,
    kind: 'line',
    tone: TREND_TONE_CYCLE[index % TREND_TONE_CYCLE.length] ?? 'neutral',
    values: valuesByGroup[index] ?? [],
  }));
  const formatValue =
    metric === 'calls'
      ? (value: number): string => COUNT_FORMATTER.format(value)
      : (value: number): string => formatCnyCents(value, value < 0);

  return (
    <Panel
      caption={title}
      count={groups.length}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="tablist"
            aria-label={t('metrics.group.metric_aria')}
            className="inline-flex gap-1"
          >
            {GROUP_METRIC_OPTIONS.map((option) => {
              const active = option === metric;
              return (
                <Button
                  key={option}
                  role="tab"
                  aria-selected={active}
                  variant={active ? 'secondary' : 'ghost'}
                  size="sm"
                  className={active ? 'bg-active text-paper' : undefined}
                  data-testid={`${testIdPrefix}-metric-${option}`}
                  onClick={() => {
                    setMetric(option);
                  }}
                >
                  {t(GROUP_METRIC_KEYS[option])}
                </Button>
              );
            })}
          </div>
          <TrendViewToggle
            view={view}
            onViewChange={setView}
            rollup={rollup}
            onRollupChange={setRollup}
            testIdPrefix={`${testIdPrefix}-trend`}
            showRollup={granularity === 'day'}
          />
        </div>
      }
      padded
      testId={`${testIdPrefix}-trends`}
    >
      {groups.length === 0 ? (
        <p className="m-0 text-body text-paper-low" data-testid={`${testIdPrefix}-trends-empty`}>
          {emptyLabel}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {cnyPerUsd === null ? (
            <div>
              <Chip tone="warn" testId={`${testIdPrefix}-rate-unavailable`}>
                {t('metrics.group.rate_unavailable')}
              </Chip>
            </div>
          ) : null}
          {groups.length > DEFAULT_VISIBLE_LIMIT && hiddenOverride === null ? (
            <p
              className="m-0 text-micro text-paper-low"
              data-testid={`${testIdPrefix}-default-visible-note`}
            >
              {t('metrics.group.default_visible_note', { count: DEFAULT_VISIBLE_LIMIT })}
            </p>
          ) : null}
          {!metricAvailable ? (
            <p
              className="m-0 rounded-lg border border-warn-line bg-warn-dim/30 p-4 text-body text-warn"
              data-testid={`${testIdPrefix}-metric-rate-unavailable`}
            >
              {t('metrics.group.rate_unavailable')}
            </p>
          ) : view === 'chart' ? (
            <TrendChart
              labels={displayLabels}
              series={chartSeries}
              hiddenKeys={hiddenKeys}
              onToggleSeries={toggleSeries}
              legendExtra={
                <span className="inline-flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`${testIdPrefix}-legend-show-all`}
                    onClick={() => {
                      setHiddenOverride(new Set());
                    }}
                  >
                    {t('metrics.chart.legend.show_all')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`${testIdPrefix}-legend-hide-all`}
                    onClick={() => {
                      setHiddenOverride(new Set(groups.map((group) => group.key)));
                    }}
                  >
                    {t('metrics.chart.legend.hide_all')}
                  </Button>
                </span>
              }
              valueFormatter={formatValue}
              ariaLabel={title}
              emptyLabel={emptyLabel}
              testId={`${testIdPrefix}-combined-chart`}
            />
          ) : (
            <GroupBucketTable
              columns={groups.flatMap((group, index) => {
                if (hiddenKeys.has(group.key)) return [];
                return [{ key: group.key, header: group.name, values: valuesByGroup[index] ?? [] }];
              })}
              labels={displayLabels}
              rollup={granularity === 'day' ? rollup : 'day'}
              formatValue={formatValue}
              emptyLabel={emptyLabel}
              testIdPrefix={testIdPrefix}
            />
          )}
        </div>
      )}
    </Panel>
  );
}

/**
 * The 表格 twin of the combined chart: one row per bucket, one column
 * per VISIBLE group (legend filtering carries into the table) plus a
 * 合计 column summing the visible groups, all in the selected metric.
 * Day-granularity buckets roll up to ISO weeks / UTC+8 months on
 * demand; hour buckets pass through (the rollup pills are hidden). The
 * caller withholds this entirely when 收费 lacks a display rate.
 */
function GroupBucketTable({
  columns,
  labels,
  rollup,
  formatValue,
  emptyLabel,
  testIdPrefix,
}: {
  readonly columns: readonly {
    readonly key: string;
    readonly header: string;
    readonly values: readonly number[];
  }[];
  readonly labels: readonly string[];
  readonly rollup: TrendRollup;
  readonly formatValue: (value: number) => string;
  readonly emptyLabel: string;
  readonly testIdPrefix: string;
}): JSX.Element {
  const t = useT();
  if (columns.length === 0) {
    return (
      <p
        className="m-0 rounded-lg bg-well p-4 text-body text-paper-low"
        data-testid={`${testIdPrefix}-bucket-table-all-hidden`}
      >
        {t('metrics.chart.all_hidden')}
      </p>
    );
  }
  const rolled = rollupDailyBuckets(
    labels,
    columns.map((column) => column.values),
    rollup,
  );
  const groupColumns: readonly TrendTableColumn[] = columns.map((column, columnIndex) => ({
    key: column.key,
    header: column.header,
    cells: rolled.labels.map((_, rowIndex) => formatValue(rolled.series[columnIndex]?.[rowIndex] ?? 0)),
  }));
  const totalColumn: TrendTableColumn = {
    key: 'total',
    header: t('metrics.models.table.col.total'),
    cells: rolled.labels.map((_, rowIndex) =>
      formatValue(rolled.series.reduce((sum, values) => sum + (values[rowIndex] ?? 0), 0)),
    ),
  };
  return (
    <TrendBucketTable
      bucketHeader={t('metrics.chart.col.bucket')}
      labels={rolled.labels}
      columns={[...groupColumns, totalColumn]}
      emptyLabel={emptyLabel}
      note={rollup === 'day' ? undefined : t('metrics.chart.rollup.note')}
      testId={`${testIdPrefix}-bucket-table`}
    />
  );
}
